'use client';

import { useState, useEffect } from 'react';
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import Spinner from './Spinner';
import ForgotPasswordModal from './ForgotPasswordModal';
import BanNotice from './BanNotice';
import FingerprintBanNotice from './FingerprintBanNotice';
import { validatePassword, checkPasswordStrength } from '@/lib/passwordValidation';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'login' | 'signup';
  showVerifiedMessage?: boolean;
  showExpiredMessage?: boolean;
}

export default function AuthModal({ isOpen, onClose, defaultMode = 'login', showVerifiedMessage = false, showExpiredMessage = false }: AuthModalProps) {
  const { login, googleLogin, signup, verify2FA } = useAuth();
  const router = useRouter();
  // Google sign-in is only offered when a public client id is configured at build.
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  const [mode, setMode] = useState<'login' | 'signup'>(defaultMode);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mounted, setMounted] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const [banData, setBanData] = useState<{ bannedUntil: string | null; bannedReason: string } | null>(null);
  const [showFingerprintBan, setShowFingerprintBan] = useState(false);
  // Pending referral code from /r/[code] landing page — purely cosmetic in
  // this component (the auth-client reads it from localStorage on submit).
  const [pendingReferral, setPendingReferral] = useState<string | null>(null);
  // Admin 2FA challenge step: when set, the modal shows a 6-digit code input
  // instead of the email/password form (the password step already passed).
  const [twoFAChallenge, setTwoFAChallenge] = useState<string | null>(null);
  const [twoFACode, setTwoFACode] = useState('');

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      document.body.style.overflow = 'hidden';
      try {
        setPendingReferral(localStorage.getItem('referralCode'));
      } catch {}
    } else {
      document.body.style.overflow = 'unset';
      setTimeout(() => setMounted(false), 300);
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  useEffect(() => {
    setMode(defaultMode);
    setError('');
    setEmail('');
    setDisplayName('');
    setPassword('');
    setConfirmPassword('');
    setAgreedToTerms(false);
    setTwoFAChallenge(null);
    setTwoFACode('');
  }, [defaultMode, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (mode === 'signup') {
      // Validate password BEFORE sending to API (per guide requirements)
      const validation = validatePassword(password);
      if (!validation.isValid) {
        setError('Password does not meet requirements');
        setPasswordErrors(validation.errors);
        setLoading(false);
        return;
      }

      if (password !== confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      if (!agreedToTerms) {
        setError('You must agree to the Terms of Service and Fair Use Policy');
        setLoading(false);
        return;
      }

      try {
        const result = await signup(email, displayName, password);

        if (result.error) {
          // Check if fingerprint is banned
          if (result.code === 'FINGERPRINT_BANNED') {
            setShowFingerprintBan(true);
            setLoading(false);
            return;
          }
          
          setError(result.error);
          setLoading(false);
          return;
        }

        // Success - redirect to email verification with userId
        // Handle both new signup (201) and re-signup within 5 min (200)
        const userId = result.user?.id;
        
        // Check if this is a re-signup case (guide line 80)
        if (result.message?.includes('already exists')) {
          console.log('Re-signup successful - token recovered');
        }
        
        router.push(`/verify-email${userId ? `?userId=${userId}` : ''}`);
        onClose();
      } catch (err) {
        setError('An error occurred');
      } finally {
        setLoading(false);
      }
    } else {
      // Login
      try {
        const result = await login(email, password);

        // Admin 2FA gate: account is enrolled — switch to the code-entry step.
        if (result.requires2FA) {
          setTwoFAChallenge(result.challengeToken || null);
          setTwoFACode('');
          setError('');
          setLoading(false);
          return;
        }

        // Admin account that hasn't enrolled 2FA yet — enrollment (QR setup) is
        // only supported in the admin dashboard, so point them there.
        if (result.requires2FASetup) {
          setError('This admin account needs two-factor setup. Please sign in at the admin dashboard first.');
          setLoading(false);
          return;
        }

        if (result.error) {
          // Check if fingerprint is banned
          if (result.code === 'FINGERPRINT_BANNED') {
            setShowFingerprintBan(true);
            setLoading(false);
            return;
          }
          
          // Check if account is banned
          if (result.code === 'ACCOUNT_BANNED') {
            setBanData({
              bannedUntil: result.bannedUntil || null,
              bannedReason: result.bannedReason || 'No reason provided',
            });
            setLoading(false);
            return;
          }
          
          // Check if email verification is required
          if (result.code === 'EMAIL_NOT_VERIFIED') {
            // Redirect to verification page with userId
            router.push(`/verify-email?userId=${result.userId}`);
            onClose();
            return;
          }
          
          setError(result.error);
        } else {
          router.push('/dashboard');
          onClose();
        }
      } catch (err) {
        setError('An error occurred');
      } finally {
        setLoading(false);
      }
    }
  };

  // Google sign-in: exchange the Google credential for an Ultragoon session,
  // reusing the same 2FA / ban / redirect handling as password login.
  const handleGoogleCredential = async (credential?: string) => {
    if (!credential) {
      setError('Google sign-in failed. Please try again.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const result = await googleLogin(credential);

      if (result.requires2FA) {
        setTwoFAChallenge(result.challengeToken || null);
        setTwoFACode('');
        setError('');
        setLoading(false);
        return;
      }
      if (result.requires2FASetup) {
        setError('This admin account needs two-factor setup. Please sign in at the admin dashboard first.');
        setLoading(false);
        return;
      }
      if (result.error) {
        if (result.code === 'FINGERPRINT_BANNED') {
          setShowFingerprintBan(true);
          setLoading(false);
          return;
        }
        if (result.code === 'ACCOUNT_BANNED') {
          setBanData({
            bannedUntil: result.bannedUntil || null,
            bannedReason: result.bannedReason || 'No reason provided',
          });
          setLoading(false);
          return;
        }
        setError(result.error);
      } else {
        router.push('/dashboard');
        onClose();
      }
    } catch {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!twoFAChallenge) return;

    if (!/^\d{6}$/.test(twoFACode.trim())) {
      setError('Enter the 6-digit code from your authenticator app');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const result = await verify2FA(twoFAChallenge, twoFACode.trim());

      if (result.success) {
        router.push('/dashboard');
        onClose();
      } else {
        setError(result.error || 'Invalid verification code');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const cancel2FA = () => {
    setTwoFAChallenge(null);
    setTwoFACode('');
    setError('');
  };

  if (!mounted && !isOpen) return null;

  return (
    <>
      {/* Fingerprint Ban Notice Modal (highest z-index) */}
      {showFingerprintBan && (
        <FingerprintBanNotice
          onClose={() => {
            setShowFingerprintBan(false);
            onClose();
          }}
        />
      )}

      {/* Account Ban Notice Modal (higher z-index) */}
      {banData && (
        <BanNotice
          bannedUntil={banData.bannedUntil}
          bannedReason={banData.bannedReason}
          onClose={() => {
            setBanData(null);
            onClose();
          }}
        />
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <ForgotPasswordModal
          isOpen={showForgotPassword}
          onClose={() => setShowForgotPassword(false)}
          onSuccess={() => {
            setShowForgotPassword(false);
            setMode('login');
          }}
        />
      )}

      <div
        className={`fixed inset-0 z-[100] flex items-center justify-center p-3 sm:p-4 transition-all duration-300 ease-out ${
          isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
      {/* Backdrop */}
      <div
        className={`absolute inset-0 bg-black/80 backdrop-blur-sm transition-all duration-300 ease-out ${
          isOpen ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div
        className={`relative bg-[#0f0f0f] rounded-lg p-4 sm:p-5 w-full max-w-[340px] shadow-2xl transition-all duration-300 ease-out max-h-[90vh] overflow-y-auto ${
          isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-90 opacity-0 translate-y-8'
        }`}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-white/50 hover:text-white/90 transition-colors duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-4 w-4"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="text-center mb-3 sm:mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-white">
            {twoFAChallenge ? 'two-factor authentication' : mode === 'login' ? 'welcome back' : 'create account'}
          </h2>
        </div>

        {twoFAChallenge ? (
          /* 2FA code step (admin accounts with two-factor enabled) */
          <form onSubmit={handleVerify2FA} className="space-y-3">
            {error && (
              <div className="p-2 bg-red-500/20 rounded-lg text-red-400 text-[11px] sm:text-xs animate-fadeIn">
                {error}
              </div>
            )}

            <p className="text-[11px] sm:text-xs text-white/60 text-center">
              Enter the 6-digit code from your authenticator app.
            </p>

            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              value={twoFACode}
              onChange={(e) => {
                setTwoFACode(e.target.value.replace(/\D/g, '').slice(0, 6));
                if (error) setError('');
              }}
              maxLength={6}
              className="w-full px-3 py-2 bg-white/5 rounded-lg text-center text-lg tracking-[0.4em] text-white placeholder:text-white/30 placeholder:tracking-normal focus:outline-none focus:bg-white/10 transition-all duration-200"
              placeholder="000000"
            />

            <button
              type="submit"
              disabled={loading || twoFACode.length !== 6}
              className="w-full py-2 bg-white text-black font-medium rounded-lg hover:bg-white/90 active:scale-95 sm:hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 cursor-pointer flex items-center justify-center gap-2 text-sm"
            >
              {loading ? (
                <>
                  <Spinner size={16} />
                  <span>verifying...</span>
                </>
              ) : (
                <span>verify</span>
              )}
            </button>

            <button
              type="button"
              onClick={cancel2FA}
              className="w-full text-center text-[11px] sm:text-xs text-white/50 hover:text-white/80 transition-colors"
            >
              back to login
            </button>
          </form>
        ) : (
        <>
        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-2 sm:space-y-2.5">
          {showExpiredMessage && !showVerifiedMessage && (
            <div className="p-2 bg-amber-500/15 rounded-lg text-amber-400 text-[11px] sm:text-xs animate-fadeIn">
              Your session expired. Please log in again to continue.
            </div>
          )}
          {showVerifiedMessage && (
            <div className="p-2 bg-green-500/20 rounded-lg text-green-400 text-[11px] sm:text-xs animate-fadeIn">
              ✓ Email verified! Please login to continue.
            </div>
          )}
          
          {error && (
            <div className="p-2 bg-red-500/20 rounded-lg text-red-400 text-[11px] sm:text-xs animate-fadeIn">
              {error}
            </div>
          )}

          {mode === 'signup' && pendingReferral && (
            <div className="p-2 bg-purple-500/20 rounded-lg text-[11px] sm:text-xs flex items-center gap-2 animate-fadeIn">
              <svg className="w-3.5 h-3.5 text-purple-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
              </svg>
              <span className="text-white/80">
                Referred by <span className="text-purple-300 font-semibold">@{pendingReferral}</span>
              </span>
            </div>
          )}

          <div>
            <label className="block text-xs text-white/70 mb-1">email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-3 py-2 bg-white/5 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 transition-all duration-200"
              placeholder="your@email.com"
            />
          </div>

          {mode === 'signup' && (
            <div>
              <label className="block text-xs text-white/70 mb-1">display name</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => {
                  // Only allow letters, numbers, and underscores, and prevent starting with underscore
                  let value = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                  if (value.startsWith('_')) {
                    value = value.substring(1);
                  }
                  setDisplayName(value);
                }}
                required
                minLength={3}
                maxLength={20}
                pattern="[a-zA-Z0-9][a-zA-Z0-9_]*"
                className="w-full px-3 py-2 bg-white/5 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 transition-all duration-200"
                placeholder="username123"
              />
              <p className="text-[10px] sm:text-xs text-white/30 mt-1">3-20 chars, letters/numbers/underscores, can't start with _</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs text-white/70">password</label>
              {mode === 'login' && (
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-xs text-purple-400 hover:text-purple-300 transition-colors"
                >
                  Forgot password?
                </button>
              )}
            </div>
            <input
              type="password"
              value={password}
              onChange={(e) => {
                const newPassword = e.target.value;
                setPassword(newPassword);
                // Clear password errors when user types
                if (passwordErrors.length > 0) {
                  setPasswordErrors([]);
                }
                // Clear general error if it's password-related
                if (error.includes('Password') || error.includes('password')) {
                  setError('');
                }
              }}
              required
              minLength={mode === 'signup' ? 8 : undefined}
              className={`w-full px-3 py-2 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none transition-all duration-200 ${
                passwordErrors.length > 0 ? 'bg-red-500/10 focus:bg-red-500/15' : 'bg-white/5 focus:bg-white/10'
              }`}
              placeholder="••••••••"
            />
            {mode === 'signup' && password && (
              <>
                {/* Password Strength Indicator */}
                {(() => {
                  const strength = checkPasswordStrength(password);
                  return (
                    <div className="mt-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-white/50">Strength:</span>
                        <span className="text-[10px] font-medium" style={{ color: strength.color }}>
                          {strength.label}
                        </span>
                      </div>
                      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-300"
                          style={{
                            width: `${strength.percentage}%`,
                            backgroundColor: strength.color,
                          }}
                        />
                      </div>
                    </div>
                  );
                })()}
              </>
            )}
            {mode === 'signup' && passwordErrors.length > 0 && (
              <div className="mt-2 p-2 bg-red-500/15 rounded text-[10px] text-red-400">
                <p className="font-medium mb-1">Password must have:</p>
                <ul className="list-disc list-inside space-y-0.5">
                  {passwordErrors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              </div>
            )}
            {mode === 'signup' && passwordErrors.length === 0 && password.length > 0 && (
              <p className="text-[10px] sm:text-xs text-white/30 mt-1">
                ✓ Min 8 chars, uppercase, lowercase, number
              </p>
            )}
          </div>

          {mode === 'signup' && (
            <>
              <div>
                <label className="block text-xs text-white/70 mb-1">
                  confirm password
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-3 py-2 bg-white/5 rounded-lg text-sm text-white placeholder:text-white/30 focus:outline-none focus:bg-white/10 transition-all duration-200"
                  placeholder="••••••••"
                />
              </div>
              
              {/* Terms Agreement Checkbox */}
              <div className="flex items-start gap-2 p-2.5 sm:p-3 bg-white/[0.05] rounded-lg">
                <input
                  type="checkbox"
                  id="terms-agreement"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  required
                  className="mt-0.5 w-3.5 h-3.5 sm:w-4 sm:h-4 rounded border-white/20 bg-white/5 text-purple-500 focus:ring-2 focus:ring-purple-500/50 focus:ring-offset-0 transition-all cursor-pointer flex-shrink-0"
                />
                <label htmlFor="terms-agreement" className="text-[10px] sm:text-xs text-white/60 leading-relaxed cursor-pointer select-none">
                  I agree to the{' '}
                  <a 
                    href="/terms" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Terms of Service
                  </a>{' '}
                  and{' '}
                  <a 
                    href="/fair-use" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-400 hover:text-purple-300 underline"
                    onClick={(e) => e.stopPropagation()}
                  >
                    Fair Use Policy
                  </a>
                  . I understand that illegal content is monitored and will be reported to authorities.
                </label>
              </div>
            </>
          )}

          <button
            type="submit"
            disabled={loading || (mode === 'signup' && !agreedToTerms)}
            className="w-full py-2 bg-white text-black font-medium rounded-lg hover:bg-white/90 active:scale-95 sm:hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100 cursor-pointer flex items-center justify-center gap-2 mt-2 sm:mt-3 text-sm"
          >
            {loading ? (
              <>
                <Spinner size={16} />
                <span>{mode === 'login' ? 'signing in...' : 'creating account...'}</span>
              </>
            ) : (
              <span>{mode === 'login' ? 'sign in' : 'sign up'}</span>
            )}
          </button>
        </form>

        {/* Google sign-in (only when a public client id is configured) */}
        {googleClientId && (
          <div className="mt-3">
            <div className="flex items-center gap-3">
              <div className="h-px flex-1 bg-white/10" />
              <span className="text-[10px] uppercase tracking-wide text-white/30">or</span>
              <div className="h-px flex-1 bg-white/10" />
            </div>
            <GoogleOAuthProvider clientId={googleClientId}>
              <div className="mt-3 flex justify-center [color-scheme:light]">
                <GoogleLogin
                  onSuccess={(cred) => handleGoogleCredential(cred.credential)}
                  onError={() => setError('Google sign-in failed. Please try again.')}
                  theme="filled_black"
                  shape="pill"
                  text={mode === 'login' ? 'signin_with' : 'signup_with'}
                  width="320"
                />
              </div>
            </GoogleOAuthProvider>
          </div>
        )}

        {/* Toggle mode */}
        <div className="mt-2.5 sm:mt-3 text-center text-[11px] sm:text-xs text-white/50">
          {mode === 'login' ? (
            <>
              don't have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('signup');
                  setError('');
                }}
                className="text-white hover:underline"
              >
                sign up
              </button>
            </>
          ) : (
            <>
              already have an account?{' '}
              <button
                type="button"
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
                className="text-white hover:underline"
              >
                sign in
              </button>
            </>
          )}
        </div>
        </>
        )}
      </div>
    </div>
    </>
  );
}

